import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Download, Plus, Trash2, Waves } from "lucide-react";

import { ApiError, formatDate, formatDateTime } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  Select,
  TableWrap,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import {
  INFRA_LABELS,
  TIPO_OBSTRUCCION_LABELS,
  crearObstruccion,
  descargarCsvObstrucciones,
  eliminarObstruccion,
  fetchObstrucciones,
  type InfraestructuraAfectada,
  type ObstruccionCreateInput,
  type TipoObstruccion,
} from "./api";

const INFRAS: InfraestructuraAfectada[] = [
  "InstalacionDomiciliaria",
  "UnionDomiciliaria",
  "Colector",
  "CamaraInspeccion",
  "TramoRedEntreCamaras",
];

const MATERIALES = [
  "PVC",
  "PEAD",
  "Hormigon",
  "GresCeramico",
  "Fibrocemento",
  "HierroFundido",
  "Otro",
];

const FORM_ID = "crear-obstruccion-form";

export function ObstruccionesPanel(): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [tipo, setTipo] = useState<TipoObstruccion | "">("");
  const [infra, setInfra] = useState<InfraestructuraAfectada | "">("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [crearAbierto, setCrearAbierto] = useState(false);
  const [confirmarId, setConfirmarId] = useState<number | null>(null);

  const filtros = {
    tipo: tipo || null,
    infraestructura: infra || null,
    desde: desde || null,
    hasta: hasta || null,
    limit: 100,
  };

  const listQ = useQuery({
    queryKey: ["obstrucciones", filtros],
    queryFn: () => fetchObstrucciones(filtros),
  });

  const exportMut = useMutation({
    mutationFn: () => descargarCsvObstrucciones(filtros),
    onSuccess: () => toast.success("CSV descargado"),
    onError: () => toast.error("No se pudo exportar"),
  });

  const eliminarMut = useMutation({
    mutationFn: (id: number) => eliminarObstruccion(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["obstrucciones"] });
      toast.success("Obstrucción eliminada");
      setConfirmarId(null);
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "Error al eliminar";
      toast.error(msg);
    },
  });

  const items = listQ.data?.items ?? [];

  return (
    <div className="space-y-4 pt-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-neutral-800">
            Obstrucciones de alcantarillado
          </h2>
          <p className="text-xs text-neutral-500">
            Sólo eventos imprevistos (Manual SISS Cont. Alc., cap. 5). Las
            programadas se gestionan por comunicación, no se registran.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            icon={Download}
            onClick={() => exportMut.mutate()}
            disabled={exportMut.isPending}
          >
            CSV (SISS)
          </Button>
          <Button icon={Plus} onClick={() => setCrearAbierto(true)}>
            Nueva
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Field label="Tipo">
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoObstruccion | "")}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
          >
            <option value="">Todos</option>
            <option value="FuerzaMayor">Fuerza mayor</option>
            <option value="CasoFortuito">Caso fortuito</option>
          </select>
        </Field>
        <Field label="Infraestructura">
          <select
            value={infra}
            onChange={(e) =>
              setInfra(e.target.value as InfraestructuraAfectada | "")
            }
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
          >
            <option value="">Todas</option>
            {INFRAS.map((i) => (
              <option key={i} value={i}>
                {INFRA_LABELS[i]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Desde">
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Hasta">
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="w-full rounded-md border border-neutral-450 px-2 py-1.5 text-sm"
          />
        </Field>
      </div>

      {listQ.isPending ? (
        <Cargando />
      ) : listQ.isError ? (
        <ErrorCarga error={listQ.error} onReintentar={() => listQ.refetch()} />
      ) : items.length === 0 ? (
        <p className="text-sm text-neutral-500">Sin obstrucciones registradas.</p>
      ) : (
        <TableWrap label="Obstrucciones">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-right">N°</th>
                <th scope="col" className="px-3 py-2 text-left">Inicio</th>
                <th scope="col" className="px-3 py-2 text-left">Tipo</th>
                <th scope="col" className="px-3 py-2 text-left">Infra</th>
                <th scope="col" className="px-3 py-2 text-left">Motivo</th>
                <th scope="col" className="px-3 py-2 text-right">Usuarios</th>
                <th scope="col" className="px-3 py-2 text-left">Indicadores</th>
                <th scope="col" className="px-3 py-2 text-left">Aviso SISS</th>
                <th scope="col" className="w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-neutral-50">
                  <td className="px-3 py-2 text-right tabular-nums text-neutral-500">
                    {it.numero_registro}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {formatDateTime(it.fecha_inicio)}
                  </td>
                  <td className="px-3 py-2">
                    {TIPO_OBSTRUCCION_LABELS[it.tipo]}
                  </td>
                  <td className="px-3 py-2 text-xs text-neutral-600">
                    {INFRA_LABELS[it.infraestructura_afectada]}
                  </td>
                  <td className="px-3 py-2 max-w-md truncate" title={it.motivo}>
                    {it.motivo}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {it.usuarios_afectados}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex flex-wrap gap-1">
                      {it.afloramiento && (
                        <Badge tone="amber">
                          <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
                          Afloró
                        </Badge>
                      )}
                      {it.alcanzo_curso_agua && (
                        <Badge tone="rose">Curso de agua</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-neutral-600">
                    {it.fecha_aviso_siss
                      ? formatDate(it.fecha_aviso_siss)
                      : "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <Button
                      variant="linkDanger"
                      size="sm"
                      icon={Trash2}
                      iconOnly
                      aria-label="Eliminar obstrucción"
                      onClick={() => setConfirmarId(it.id)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}

      {crearAbierto && (
        <CrearObstruccionModal
          onClose={() => setCrearAbierto(false)}
          onCreated={() => {
            setCrearAbierto(false);
            qc.invalidateQueries({ queryKey: ["obstrucciones"] });
            toast.success("Obstrucción registrada");
          }}
        />
      )}

      <ConfirmDialog
        open={confirmarId !== null}
        title="Eliminar obstrucción"
        description="Esta acción elimina el registro de obstrucción. El evento queda en la bitácora de auditoría."
        confirmLabel="Eliminar"
        loading={eliminarMut.isPending}
        onConfirm={() => {
          if (confirmarId !== null) eliminarMut.mutate(confirmarId);
        }}
        onCancel={() => setConfirmarId(null)}
      />
    </div>
  );
}

function CrearObstruccionModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}): JSX.Element {
  const toast = useToast();
  const [tipo, setTipo] = useState<TipoObstruccion>("CasoFortuito");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaTermino, setFechaTermino] = useState("");
  const [motivo, setMotivo] = useState("");
  const [ubicacion, setUbicacion] = useState("");
  const [usuarios, setUsuarios] = useState("0");
  const [afloramiento, setAfloramiento] = useState(false);
  const [cursoAgua, setCursoAgua] = useState(false);
  const [infra, setInfra] = useState<InfraestructuraAfectada>("Colector");
  const [diametro, setDiametro] = useState("");
  const [material, setMaterial] = useState("");
  const [avisoSiss, setAvisoSiss] = useState("");
  const [observaciones, setObservaciones] = useState("");

  const mut = useMutation({
    mutationFn: (input: ObstruccionCreateInput) => crearObstruccion(input),
    onSuccess: () => onCreated(),
    onError: (err: unknown) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo registrar";
      toast.error(msg);
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (motivo.trim().length < 3) {
      toast.error("Motivo debe tener al menos 3 caracteres");
      return;
    }
    if (!fechaInicio) {
      toast.error("Fecha de inicio obligatoria");
      return;
    }
    mut.mutate({
      tipo,
      fecha_inicio: new Date(fechaInicio).toISOString(),
      fecha_termino: fechaTermino ? new Date(fechaTermino).toISOString() : null,
      motivo: motivo.trim(),
      ubicacion_falla: ubicacion.trim() || null,
      usuarios_afectados: Number(usuarios) || 0,
      afloramiento,
      alcanzo_curso_agua: cursoAgua,
      infraestructura_afectada: infra,
      diametro_mm: diametro ? Number(diametro) : null,
      material: material || null,
      fecha_aviso_siss: avisoSiss || null,
      observaciones: observaciones.trim() || null,
    });
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      tipo, fechaInicio, fechaTermino, motivo, ubicacion, usuarios, afloramiento,
      cursoAgua, infra, diametro, material, avisoSiss, observaciones,
    ],
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Nueva obstrucción de alcantarillado"
      icon={Waves}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form={FORM_ID} disabled={mut.isPending}>
            {mut.isPending ? "Guardando…" : "Registrar"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Tipo"
            required
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoObstruccion)}
          >
            <option value="FuerzaMayor">Fuerza mayor</option>
            <option value="CasoFortuito">Caso fortuito</option>
          </Select>
          <Select
            label="Infraestructura"
            required
            value={infra}
            onChange={(e) =>
              setInfra(e.target.value as InfraestructuraAfectada)
            }
          >
            {INFRAS.map((i) => (
              <option key={i} value={i}>
                {INFRA_LABELS[i]}
              </option>
            ))}
          </Select>
          <Input
            label="Inicio"
            required
            type="datetime-local"
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.target.value)}
          />
          <Input
            label="Término"
            type="datetime-local"
            value={fechaTermino}
            onChange={(e) => setFechaTermino(e.target.value)}
          />
        </div>
        <Textarea
          label="Motivo"
          required
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={2}
          placeholder="Raíces, residuos, conexión defectuosa…"
        />
        <Input
          label="Ubicación"
          value={ubicacion}
          onChange={(e) => setUbicacion(e.target.value)}
        />
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Usuarios afectados"
            type="number"
            value={usuarios}
            onChange={(e) => setUsuarios(e.target.value)}
            min={0}
          />
          <Input
            label="Diámetro (mm)"
            type="number"
            value={diametro}
            onChange={(e) => setDiametro(e.target.value)}
          />
          <Select
            label="Material"
            value={material}
            onChange={(e) => setMaterial(e.target.value)}
          >
            <option value="">—</option>
            {MATERIALES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </div>
        <div className="flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={afloramiento}
              onChange={(e) => setAfloramiento(e.target.checked)}
            />
            Afloró a superficie
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
            <input
              type="checkbox"
              checked={cursoAgua}
              onChange={(e) => setCursoAgua(e.target.checked)}
            />
            Alcanzó curso de agua
          </label>
        </div>
        <Input
          label="Aviso a SISS (≤24h)"
          type="date"
          value={avisoSiss}
          onChange={(e) => setAvisoSiss(e.target.value)}
        />
        <Textarea
          label="Observaciones"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows={2}
        />
      </form>
      {dialogoDescarte}
    </Modal>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-neutral-600">
        {label}
      </label>
      {children}
    </div>
  );
}
